import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';
import { OPINION_DECISION } from '@/pages/customer/chat/constants/opinion.constants';

export const CompanionRoot = styled.div`
	display: grid;
	gap: 12px;
`;

export const CompanionGlass = styled.div`
	display: grid;
	gap: 12px;
	padding: 10px;
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 16px;
	background:
		linear-gradient(135deg, rgba(255, 255, 255, 0.095), rgba(255, 255, 255, 0.025)),
		rgba(13, 6, 18, 0.58);
	box-shadow: 0 18px 38px rgba(0, 0, 0, 0.24), 0 0 20px rgba(255, 77, 242, 0.08);
	backdrop-filter: blur(22px) saturate(1.12);
`;

export const CompanionPanelHeading = styled.div`
	min-height: 36px;
	padding: 0 4px 6px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
`;

export const CompanionCloseButton = styled.button`
	width: 32px;
	min-width: 32px;
	height: 32px;
	margin: 0;
	border: 1px solid rgba(255, 255, 255, 0.09);
	border-radius: 999px;
	padding: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: rgba(255, 245, 255, 0.7);
	background: rgba(255, 255, 255, 0.045);
	backdrop-filter: blur(16px);
	cursor: pointer;
	transition:
		border-color 160ms ease,
		background 160ms ease,
		color 160ms ease,
		box-shadow 160ms ease;

	&:hover,
	&:focus-visible {
		color: #ffffff;
		border-color: rgba(255, 119, 244, 0.24);
		background: rgba(255, 77, 242, 0.11);
		box-shadow: 0 0 14px rgba(255, 77, 242, 0.08);
	}
`;

export const CompanionCloseArrow = styled.span`
	flex: 0 0 auto;
	width: 6px;
	height: 6px;
	border-top: 2px solid currentColor;
	border-right: 2px solid currentColor;
	transform: rotate(45deg);
`;

export const CompanionMiniProfile = styled.div`
	display: grid;
	grid-template-columns: 92px minmax(0, 1fr);
	gap: 12px;
	color: ${THEME.text};
`;

export const CompanionProfileImage = styled.img`
	width: 92px;
	aspect-ratio: 3 / 4;
	border-radius: 12px;
	object-fit: cover;
	box-shadow: 0 0 22px rgba(255, 77, 242, 0.12);
`;

export const CompanionProfileStack = styled.div`
	display: grid;
	gap: 8px;
`;

export const CompanionTextBlock = styled.span`
	min-width: 0;
	display: block;
`;

export const CompanionDetails = CompanionTextBlock;

export const CompanionActions = styled.div`
	display: flex;
	justify-content: center;
	gap: 5px;
`;

export const CompanionActionButton = styled.button`
	width: 27px;
	height: 27px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 255, 255, 0.09);
	border-radius: 9px;
	color: rgba(255, 236, 255, 0.66);
	background: rgba(255, 255, 255, 0.045);
	backdrop-filter: blur(14px);
	cursor: pointer;
	padding: 0;
	transition:
		color 160ms ease,
		border-color 160ms ease,
		background 160ms ease,
		box-shadow 160ms ease;

	svg {
		width: 16px;
		height: 16px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.8;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&:hover,
	&:focus-visible {
		color: #ffffff;
		border-color: rgba(255, 119, 244, 0.28);
		background: rgba(255, 77, 242, 0.13);
		box-shadow: 0 0 20px rgba(255, 77, 242, 0.17);
	}

	${(props) =>
		props.$info &&
		css`
			color: rgba(255, 236, 255, 0.7);

			svg {
				width: 14px;
				height: 14px;
			}

			&:hover,
			&:focus-visible {
				box-shadow: 0 0 16px rgba(255, 77, 242, 0.14);
			}
		`}

	${(props) =>
		props.$active &&
		props.$decision === OPINION_DECISION.HAND_UP &&
		css`
			color: #22d865;
			border-color: rgba(34, 216, 101, 0.35);
			background: rgba(34, 216, 101, 0.12);
		`}

	${(props) =>
		props.$active &&
		props.$decision === OPINION_DECISION.HAND_DOWN &&
		css`
			color: #ff6b8a;
			border-color: rgba(255, 107, 138, 0.35);
			background: rgba(255, 107, 138, 0.12);
		`}
`;

export const CompanionName = styled.strong`
	display: block;
	margin: 3px 0 8px;
	font-family: ${THEME.fontDisplay};
	font-size: 1.65rem;
	line-height: 0.95;
`;

export const CompanionBio = styled.small`
	display: -webkit-box;
	overflow: hidden;
	color: rgba(245, 221, 255, 0.78);
	font-size: 0.84rem;
	line-height: 1.34;
	-webkit-line-clamp: 7;
	-webkit-box-orient: vertical;
`;

export const CompanionChip = styled.span`
	display: inline-flex;
	width: fit-content;
	margin: 14px 0 12px;
	border-radius: 999px;
	padding: 8px 13px;
	color: #ffd5fb;
	background: rgba(255, 77, 242, 0.1);
	box-shadow: inset 0 0 0 1px rgba(255, 77, 242, 0.12);
	font-size: 0.8rem;
	font-weight: 800;
`;

export const CompanionMediaSection = styled.section`
	padding-top: 4px;
`;

export const CompanionMediaTitle = styled.h3`
	margin: 0 0 12px;
	padding-bottom: 12px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.22);
	font-family: ${THEME.fontDisplay};
	font-size: 1.35rem;
	font-weight: 700;
	line-height: 1;
	color: ${THEME.text};
`;

export const CompanionMediaEmpty = styled.p`
	margin: 28px 8px 0;
	color: rgba(226, 200, 236, 0.8);
	text-align: center;
	line-height: 1.45;
	font-size: 0.88rem;
`;

export const CompanionGalleryViewport = styled.div`
	min-width: 0;
	overflow: hidden;
`;

export const CompanionGalleryControls = styled.div`
	display: grid;
	grid-template-columns: 32px minmax(0, 1fr) 32px;
	align-items: center;
	gap: 10px;
	padding-top: 10px;
`;

const galleryEnter = keyframes`
	from {
		opacity: 0;
		transform: translateX(10px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
`;

export const CompanionGalleryPage = styled.div`
	animation: ${galleryEnter} 220ms ease-out;
`;

export const CompanionGalleryGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 8px;
`;

export const CompanionGalleryNav = styled.button`
	width: 32px;
	height: 32px;
	border: 1px solid rgba(255, 255, 255, 0.09);
	border-radius: 999px;
	padding: 0;
	display: grid;
	place-items: center;
	color: rgba(255, 245, 255, 0.82);
	background: rgba(255, 255, 255, 0.045);
	backdrop-filter: blur(14px);
	cursor: pointer;
	transition:
		border-color 160ms ease,
		background 160ms ease,
		box-shadow 160ms ease,
		transform 160ms ease,
		opacity 160ms ease;

	svg {
		width: 14px;
		height: 14px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&:hover:not(:disabled),
	&:focus-visible:not(:disabled) {
		color: #fff;
		border-color: rgba(255, 119, 244, 0.28);
		background: rgba(255, 77, 242, 0.13);
		box-shadow: 0 0 16px rgba(255, 77, 242, 0.14);
	}

	&:disabled {
		opacity: 0.28;
		cursor: not-allowed;
	}
`;

export const CompanionGalleryDots = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 6px;
`;

export const CompanionGalleryDot = styled.button`
	width: ${(props) => (props.$active ? '18px' : '6px')};
	height: 6px;
	border: 0;
	border-radius: 999px;
	padding: 0;
	background: ${(props) =>
		props.$active
			? 'linear-gradient(135deg, #b026ff, #ff4df2)'
			: 'rgba(255, 255, 255, 0.18)'};
	box-shadow: ${(props) =>
		props.$active ? '0 0 12px rgba(255, 77, 242, 0.45)' : 'none'};
	cursor: pointer;
	transition:
		width 180ms ease,
		background 180ms ease,
		box-shadow 180ms ease;
`;

export const CompanionMediaItem = styled.button`
	position: relative;
	overflow: hidden;
	aspect-ratio: 1;
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 12px;
	padding: 0;
	background: rgba(255, 255, 255, 0.05);
	cursor: pointer;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`;

export const CompanionMediaVideo = styled.video`
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
`;

export const CompanionMediaPlayBadge = styled.span`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.25rem;
	color: #fff;
	text-shadow: 0 2px 8px rgba(0, 0, 0, 0.55);
	pointer-events: none;
`;

export const CompanionMediaPlaceholder = styled.div`
	aspect-ratio: 1;
	border: 1px dashed rgba(255, 255, 255, 0.14);
	border-radius: 12px;
	padding: 12px;
	display: grid;
	place-content: center;
	justify-items: center;
	gap: 8px;
	text-align: center;
	background:
		linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.015)),
		rgba(255, 255, 255, 0.03);
	color: rgba(226, 200, 236, 0.62);
	font-size: 0.72rem;
	line-height: 1.35;

	svg {
		width: 22px;
		height: 22px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.6;
		stroke-linecap: round;
		stroke-linejoin: round;
		opacity: 0.72;
	}
`;
